import type {
  RosterTemplateCatalogEntry,
  RosterTemplatesResponse,
} from './rosterTemplateTypes';

/**
 * Built-in FEMA EOC chart. Shown only while FEMA daily operations briefing is
 * on.
 */
export const FEMA_EOC_TEMPLATE_SLUG = 'fema-eoc';

/**
 * Seats that exist only on the FEMA EOC chart. Standard ICS titles stay
 * addable.
 */
export const FEMA_EOC_CUSTOM_TITLES = [
  'EOC Director',
  'Situational Awareness Section',
  'Planning Support Section',
  'Resources Support Section',
  'Center Support Section',
] as const;

/** Built-in Virginia VEST chart. Shown only while the VDEM flag is on. */
export const VDEM_VEST_TEMPLATE_SLUG = 'vdem-vest';

/**
 * Seats that exist only on the VDEM VEST chart. Standard ICS titles stay
 * addable. Keep this list aligned with the coms `vdem-vest` template.
 */
export const VDEM_CUSTOM_TITLES = [
  'STATE COORDINATOR',
  "STATE COORDINATOR'S TEAM",
  "GOVERNOR'S SITUATION ROOM",
  'VEST BUREAU CHIEF',
  'VEST LEAD',
  'HEALTH AND SAFETY OFFICER',
  'DIVERSITY AND INCLUSION OFFICER',
  'ACCESS AND FUNCTIONAL NEEDS OFFICER',
  'EXTERNAL AFFAIRS CHIEF',
  'OPS SECTION CHIEF',
  'PLANNING SECTION CHIEF',
  'LOGISTICS SECTION CHIEF',
  'VIRGINIA EMAC A-TEAM / STAGING ARM STRIKE TEAM / PODS STRIKE TEAM / PPDS STRIKE TEAM / SNS STRIKE TEAM',
  'FINANCE SECTION CHIEF',
  'RECOVERY SECTION CHIEF',
  'ELECTED OFFICIAL LIAISON',
  'JIC MANAGER',
  'NIGHT',
  'DAY',
  'SOCIAL MEDIA MONITOR (NIGHT)',
  'WRITER(S) (NIGHT)',
  'TRAFFIC MANAGER (NIGHT)',
  'WRITER(S) (DAY)',
  'SOCIAL MEDIA MONITOR (DAY)',
  'SOCIAL MEDIA PUBLISHER',
  'VIDEO/PLANNING LIAISON',
  'TRAFFIC MANAGER (DAY)',
  'ELECTED OFFICIAL LIAISON (DAY)',
  'FIELD PIO STAFF',
  'WEBMASTER',
  'GRAPHIC DESIGNER',
  'PHOTOGRAPHER',
  'NEWS CONFERENCE COORDINATOR',
  'FIELD PIO SUPERVISOR/LEAD',
  'FIELD PIO STAFF (2)',
  'REGIONAL SUPPORT BRANCH DIRECTOR',
  'EAST',
  'WEST',
  'HUMAN SERVICES BRANCH DIRECTOR',
  'ESF 6,8,11,17',
  'EMERGENCY SERVICES BRANCH DIRECTOR',
  'ESF 4,9,10,13,16',
  'INFRASTRUCTURE BRANCH DIRECTOR',
  'ESF 1,2,3,12,14',
  'RSFs',
  'DEPUTY PLANNING SECTION CHIEF',
  'SITUATION UNIT LEADER',
  'INFO COLLECTION',
  'GIS UNIT LEADER',
  'PLANNER',
  'PRIVATE SECTOR PARTNERSHIPS/CONTRACTS',
  'RESOURCE MANAGEMENT AND COORDINATION UNIT',
  'RESOURCE MANAGEMENT SPECIALISTS (2)',
  'ACCOMMODATIONS UNIT',
  'PRIVATE SECTOR LIAISON',
  'PROCUREMENT UNIT',
  'INFORMATION SYSTEMS UNIT',
  'CRISIS MANAGEMENT SYSTEMS SUPPORT',
  'VITA SUPPORT TEAM',
  'LOGISTICS SUPPORT UNIT',
  'SUPPORT SPECIALIST',
  'VITA SUPPORT TEAM (LOGISTICS)',
  'VEOC FACILITY AND GENERAL SUPPORT',
  'EXTENDED FACILITIES',
  'PERSONNEL',
  'DEPUTY RECOVERY SECTION CHIEF',
  'RECOVERY COORDINATORS (4)',
  'DEPUTY RECOVERY CHIEF',
  'RECOVERY COORDINATORS (4) (DEPUTY)',
] as const;

/** The badge the API stamps on templates that came from the org's saved library. */
export const ORG_TEMPLATE_BADGE_CATEGORY = 'organization';

export function isOrgTemplate(entry: RosterTemplateCatalogEntry): boolean {
  return entry.category === ORG_TEMPLATE_BADGE_CATEGORY;
}

const ORG_TEMPLATE_SLUG_PREFIX = 'org:';

/**
 * The library row id behind an org template's catalog slug, or null for a
 * built-in.
 */
export function orgTemplateIdFromSlug(slug: string): string | null {
  if (!slug.startsWith(ORG_TEMPLATE_SLUG_PREFIX)) return null;
  return slug.slice(ORG_TEMPLATE_SLUG_PREFIX.length).trim() || null;
}

/** Split API catalog entries into Load templates vs attach-only expansions. */
export function partitionRosterTemplates(
  entries: RosterTemplateCatalogEntry[]
) {
  const templates: RosterTemplateCatalogEntry[] = [];
  const expansions: RosterTemplateCatalogEntry[] = [];
  for (const entry of entries) {
    if (entry.attachOnly) expansions.push(entry);
    else templates.push(entry);
  }
  return { templates, expansions };
}

/**
 * Split a list into the org's own saved templates and the built-in catalog, so
 * the sidebar can lead with what the organization authored.
 */
export function partitionBySource(entries: RosterTemplateCatalogEntry[]) {
  const organization: RosterTemplateCatalogEntry[] = [];
  const builtIn: RosterTemplateCatalogEntry[] = [];
  for (const entry of entries) {
    if (isOrgTemplate(entry)) organization.push(entry);
    else builtIn.push(entry);
  }
  return { organization, builtIn };
}

/**
 * Drop the FEMA EOC template and its custom seats when the briefing flag is
 * off.
 */
export function filterRosterCatalogForFemaOps<
  T extends Pick<
    RosterTemplatesResponse,
    'templates' | 'addablePositionTitles'
  >,
>(catalog: T, femaOps: boolean): T {
  if (femaOps) return catalog;
  const hidden = new Set<string>(FEMA_EOC_CUSTOM_TITLES);
  return {
    ...catalog,
    templates: catalog.templates.filter(
      (entry) => entry.slug !== FEMA_EOC_TEMPLATE_SLUG
    ),
    addablePositionTitles: catalog.addablePositionTitles.filter(
      (title) => !hidden.has(title)
    ),
  };
}

/** Drop the VDEM VEST template and its custom seats when the VDEM flag is off. */
export function filterRosterCatalogForVdem<
  T extends Pick<
    RosterTemplatesResponse,
    'templates' | 'addablePositionTitles'
  >,
>(catalog: T, vdem: boolean): T {
  if (vdem) return catalog;
  const hidden = new Set<string>(VDEM_CUSTOM_TITLES);
  return {
    ...catalog,
    templates: catalog.templates.filter(
      (entry) => entry.slug !== VDEM_VEST_TEMPLATE_SLUG
    ),
    addablePositionTitles: catalog.addablePositionTitles.filter(
      (title) => !hidden.has(title)
    ),
  };
}
